/* eslint-disable */
// Vendored product-supplied canvas animation, trimmed to the single "nebula" scene we use:
// the NEPTUNE wordmark forming inside a living nebula. Our deltas to the original are commented.
import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

/** NEPTUNE wordmark reconstructed as vector geometry (serif glyphs, crown, rules, diamond),
 *  sampled once into particle targets in a 1000×360 design space — never from a bitmap. */
function createLogoTargets(random: () => number) {
    const mask = document.createElement("canvas");
    mask.width = 1000; mask.height = 360;
    const pen = mask.getContext("2d", { willReadFrequently: true })!;
    pen.fillStyle = "white";
    const glyphs: Record<string, string> = {
        N: "M2 110V104H15V6H2V0H35L82 77V6H69V0H100V6H89V110H78L22 20V104H36V110Z",
        E: "M2 0H96L98 29H92C87 11 82 7 67 7H34V49H53C65 49 68 44 69 33H75V73H69C67 61 64 57 53 57H34V103H67C83 103 90 97 96 79H102L98 110H2V104H15V6H2Z",
        P: "M2 0H54C87 0 103 11 103 32C103 55 81 63 53 63H35V104H51V110H2V104H15V6H2ZM35 7V56H51C74 56 81 47 81 32C81 15 73 7 53 7Z",
        T: "M0 0H105L108 32H102C97 13 90 7 72 7H64V104H81V110H25V104H42V7H34C17 7 10 13 5 32H0Z",
        U: "M0 0H47V6H35V72C35 94 44 103 62 103C81 103 89 91 89 71V6H76V0H109V6H97V73C97 101 82 113 57 113C28 113 14 100 14 76V6H0Z"
    };
    [..."NEPTUNE"].forEach((letter, index) => {
        pen.save(); pen.translate(65 + index * 126, 155);
        pen.fill(new Path2D(glyphs[letter]), "evenodd"); pen.restore();
    });
    // Three-point crown with a raised middle jewel and rounded side jewels.
    pen.fill(new Path2D("M447 108L429 53Q448 65 459 57Q466 53 469 37L486 65Q500 79 514 65L531 37Q534 53 541 57Q552 65 571 53L553 108ZM452 116H548V127H452Z"));
    pen.fill(new Path2D("M493 18L500 3L507 18L500 34Z"));
    for (const [x, y] of [[432, 48], [568, 48], [500, 40]]) {
        pen.beginPath(); pen.arc(x, y, 7, 0, Math.PI * 2); pen.fill();
    }
    // Decorative rules and the small diamond in the supplied mark.
    pen.fillRect(60, 132, 366, 3); pen.fillRect(574, 132, 366, 3);
    pen.fillRect(153, 306, 311, 3); pen.fillRect(536, 306, 311, 3);
    pen.fill(new Path2D("M500 291L511 305L500 319L489 305Z"));
    const pixels = pen.getImageData(0, 0, 1000, 360).data;
    const targets: { x: number; y: number }[] = [];
    for (let y = 2; y < 340; y += 3) for (let x = 2; x < 1000; x += 3) {
        if (pixels[(y * 1000 + x) * 4 + 3] > 100)
            targets.push({ x: (x - 500) / 1000, y: (y - 170) / 1000 });
    }
    // Shuffle once to distribute particles uniformly across all logo features.
    for (let i = targets.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [targets[i], targets[j]] = [targets[j], targets[i]];
    }
    return targets;
}

/** Standalone React/Canvas animation; no remote assets or animation dependencies. */
export function NeptuneScene({ paused = false, className = "" }: { paused?: boolean; className?: string }) {
    const canvasRef = useRef<HTMLCanvasElement>(null), pausedRef = useRef(paused);
    useEffect(() => { pausedRef.current = paused; }, [paused]);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) return;
        let width = 1, height = 1, frame = 0, time = 0, last = performance.now(), visible = true, dead = false, pointerX = 0, pointerY = 0, smoothX = 0, smoothY = 0;
        let logoProgress = 0, logoFrom = 0, logoTo = 1, logoElapsed = 0;
        const logoDuration = 6, clickDuration = 2.6; // slow opening; snappier click transitions
        let activeDuration = logoDuration;
        let pointerPresent = false;
        let pointerAt = 0, velocityX = 0, velocityY = 0, flowSpeed = 0;
        const pointerTrail: { x: number; y: number; at: number }[] = [];
        const trailLifetime = 1100;
        const recordPointer = (now: number) => {
            if (pausedRef.current || reduceMotion) return;
            const previous = pointerTrail[pointerTrail.length - 1];
            if (previous && now - previous.at > trailLifetime) pointerTrail.length = 0;
            if (!previous || !pointerTrail.length || Math.hypot((pointerX - previous.x) * width / 2, (pointerY - previous.y) * height / 2) >= 3) {
                pointerTrail.push({ x: pointerX, y: pointerY, at: now });
                if (pointerTrail.length > 96) pointerTrail.shift();
            }
        };
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
        let reduceMotion = reduced.matches;
        const onPreference = () => { reduceMotion = reduced.matches; };
        reduced.addEventListener("change", onPreference);
        let seed = 97;
        const random = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
        const logoTargets = createLogoTargets(random);
        const particles = Array.from({ length: 4200 }, () => ({ a: random() * Math.PI * 2, b: random(), c: random(), d: random(), e: random() }));
        const pushX = new Float32Array(particles.length), pushY = new Float32Array(particles.length);
        const stars = Array.from({ length: 150 }, () => ({ x: random(), y: random(), r: random(), phase: random() * 6.28 }));
        const resize = () => { const b = canvas.getBoundingClientRect(); width = b.width; height = b.height; const dpr = Math.min(window.devicePixelRatio || 1, 1.6); canvas.width = Math.max(1, Math.round(width * dpr)); canvas.height = Math.max(1, Math.round(height * dpr)); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
        const observer = new ResizeObserver(resize);
        observer.observe(canvas);
        resize();
        const intersection = new IntersectionObserver(e => { visible = e[0].isIntersecting; });
        intersection.observe(canvas);
        const move = (e: PointerEvent) => {
            const r = canvas.getBoundingClientRect(), now = performance.now();
            const nextX = (e.clientX - r.left) / r.width * 2 - 1;
            const nextY = (e.clientY - r.top) / r.height * 2 - 1;
            if (pointerPresent && pointerAt && now - pointerAt < 120) {
                const seconds = Math.max(.008, (now - pointerAt) / 1000);
                const vx = (nextX - pointerX) * r.width / 2 / seconds;
                const vy = (nextY - pointerY) * r.height / 2 / seconds;
                const cap = Math.min(1, Math.min(r.width, r.height) * 3 / Math.max(1, Math.hypot(vx, vy)));
                velocityX += (vx * cap - velocityX) * .45;
                velocityY += (vy * cap - velocityY) * .45;
            } else { velocityX = 0; velocityY = 0; }
            pointerPresent = true; pointerAt = now; pointerX = nextX; pointerY = nextY;
            recordPointer(now);
        };
        const leave = () => { pointerPresent = false; pointerX = 0; pointerY = 0; velocityX = 0; velocityY = 0; pointerAt = 0; pointerTrail.length = 0; };
        const endTouch = (e: PointerEvent) => { if (e.pointerType === "touch") leave(); };
        const activate = () => { logoFrom = logoProgress; logoTo = logoTo > .5 ? 0 : 1; logoElapsed = 0; activeDuration = clickDuration; };
        const key = (e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); }
            if (e.key.startsWith("Arrow")) {
                e.preventDefault();
                pointerPresent = true;
                pointerX = Math.max(-1, Math.min(1, pointerX + (e.key === "ArrowRight" ? .2 : e.key === "ArrowLeft" ? -.2 : 0)));
                pointerY = Math.max(-1, Math.min(1, pointerY + (e.key === "ArrowDown" ? .2 : e.key === "ArrowUp" ? -.2 : 0)));
                recordPointer(performance.now());
            }
        };
        canvas.addEventListener("pointermove", move);
        canvas.addEventListener("pointerleave", leave);
        canvas.addEventListener("pointerdown", activate);
        canvas.addEventListener("pointerup", endTouch);
        canvas.addEventListener("pointercancel", leave);
        canvas.addEventListener("blur", leave);
        canvas.addEventListener("keydown", key);
        function glow(x: number, y: number, r: number, color: string, opacity: number) { if (r <= 0) return; const g = ctx!.createRadialGradient(x, y, 0, x, y, r); g.addColorStop(0, `rgba(${color},${opacity})`); g.addColorStop(.32, `rgba(${color},${opacity * .32})`); g.addColorStop(1, `rgba(${color},0)`); ctx!.fillStyle = g; ctx!.fillRect(x - r, y - r, r * 2, r * 2); }
        function dot(x: number, y: number, r: number, color: string) { ctx!.fillStyle = color; ctx!.beginPath(); ctx!.arc(x, y, r, 0, Math.PI * 2); ctx!.fill(); }
        const render = (now: number) => {
            if (dead) return;
            frame = requestAnimationFrame(render);
            if (!visible || document.hidden) { last = now; return; }
            if (now - last < 32) return;
            const elapsedSeconds = (now - last) / 1000;
            const dt = Math.min(elapsedSeconds, .06);
            last = now;
            const running = !pausedRef.current && !reduceMotion;
            if (running) {
                time += dt;
                smoothX += (pointerX - smoothX) * .065;
                smoothY += (pointerY - smoothY) * .065;
                // Keep the morph independent of rendering frame rate.
                logoElapsed += elapsedSeconds;
                const progress = Math.min(1, logoElapsed / activeDuration);
                const eased = progress * progress * progress * (progress * (progress * 6 - 15) + 10);
                logoProgress = logoFrom + (logoTo - logoFrom) * eased;
            }
            const t = time, w = width, h = height, s = Math.min(w, h), cx = w * .5 + smoothX * s * .045, cy = h * (h > 500 ? .36 : .46) + smoothY * s * .035;
            if (running) {
                // Speed controls wake length; its shape comes from the actual pointer history.
                if (now - pointerAt > 50) { const decay = Math.exp(-elapsedSeconds * 9); velocityX *= decay; velocityY *= decay; }
                const speed = Math.hypot(velocityX, velocityY);
                const blend = 1 - Math.exp(-elapsedSeconds * 12);
                flowSpeed += (Math.min(1, speed / Math.max(1, s * 1.5)) - flowSpeed) * blend;
            }
            ctx.fillStyle = "#03070e";
            ctx.fillRect(0, 0, w, h);
            glow(w * .5, h * .46, s * .85, "12,40,83", .35);
            for (const star of stars) {
                const a = .15 + star.r * .42 + (running ? Math.sin(t * .7 + star.phase) * .1 : 0);
                dot((star.x * w + smoothX * star.r * 9 + w) % w, (star.y * h + smoothY * star.r * 7 + h) % h, .35 + star.r * .65, `rgba(192,215,243,${a})`);
            }
            ctx.save();
            ctx.globalCompositeOperation = "lighter";
            const logoScale = Math.min(w * .94, h * 1.7);
            const m = reduceMotion ? 1 : logoProgress;
            const halfWidth = Math.min(38, s * .08);
            // Fit the opening cloud to the available space around its center, leaving room for
            // depth tilt, star glow and pointer displacement.
            const cloudPadding = Math.max(14, s * .035) + halfWidth * 1.2 + logoScale * .02;
            const cloudRadiusX = Math.max(0, Math.min(logoScale * .57 * .92, Math.min(cx, w - cx) - cloudPadding));
            const cloudRadiusY = Math.max(0, Math.min(s * .9 * .57 * .92, Math.min(cy, h - cy) - cloudPadding));
            const maxTrailLength = s * (.22 + flowSpeed * .40);
            // Build a tapered tube along recent pointer samples; each section keeps its own direction.
            const wake: { x: number; y: number; dx: number; dy: number; length2: number; r0: number; r1: number; fade: number; minX: number; maxX: number; minY: number; maxY: number }[] = [];
            if (running && pointerPresent) {
                while (pointerTrail.length && now - pointerTrail[0].at > trailLifetime) pointerTrail.shift();
                let x = (pointerX + 1) * w / 2, y = (pointerY + 1) * h / 2, traveled = 0;
                for (let j = pointerTrail.length - 1; j >= 0 && traveled < maxTrailLength; j--) {
                    const sample = pointerTrail[j];
                    let dx = (sample.x + 1) * w / 2 - x, dy = (sample.y + 1) * h / 2 - y;
                    const length = Math.hypot(dx, dy);
                    if (length < .5) continue;
                    const used = Math.min(length, maxTrailLength - traveled);
                    dx *= used / length; dy *= used / length;
                    const age = Math.min(1, (now - sample.at) / trailLifetime);
                    const fade = (1 - age) * (1 - age);
                    const r0 = halfWidth * Math.max(.08, 1 - traveled / maxTrailLength);
                    traveled += used;
                    const r1 = halfWidth * Math.max(.08, 1 - traveled / maxTrailLength);
                    wake.push({ x, y, dx, dy, length2: used * used, r0, r1, fade,
                        minX: Math.min(x, x + dx) - r0, maxX: Math.max(x, x + dx) + r0,
                        minY: Math.min(y, y + dy) - r0, maxY: Math.max(y, y + dy) + r0 });
                    x += dx; y += dy;
                }
            }
            glow(cx - s * .16, cy, s * .52, "36,54,181", .37);
            glow(cx + s * .18, cy - s * .1, s * .4, "122,40,184", .22);
            // Ribbons persist, flowing through the formed mark (our delta: not faded out with m).
            for (let j = 0; j < 30; j++) {
                const phase = j * .11;
                ctx.beginPath();
                for (let k = 0; k <= 95; k++) {
                    const u = k / 95, envelope = Math.sin(u * Math.PI);
                    const x = (u - .5) * cloudRadiusX * 1.75;
                    const y = (Math.sin(u * 8 - t * .35 + phase) * .4 * envelope + Math.cos(u * 4 + t * .2 + phase) * .28 + (j - 15) * .02) * cloudRadiusY;
                    k ? ctx.lineTo(cx + x, cy + y) : ctx.moveTo(cx + x, cy + y);
                }
                ctx.lineWidth = .75;
                ctx.strokeStyle = `hsla(${195 + j * 2.8},90%,${50 + j * .7}%,${(.14 + Math.sin(phase) * .12) * .55})`;
                ctx.stroke();
            }
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i], target = logoTargets[i % logoTargets.length];
                const angle = p.a + t * (.04 + p.d * .015), radius = .09 + Math.sqrt(p.b) * .48;
                const scatterRadius = radius / .57;
                const sx = Math.cos(angle) * scatterRadius * cloudRadiusX;
                const wave = Math.sin(angle) * .75 + Math.sin(angle * 3 + t * .28 + radius * 15) * .25;
                const sy = wave * scatterRadius * cloudRadiusY;
                // Our delta: a slice stays free-flowing as nebula; the rest forms a dense wordmark.
                const freeP = p.c < .2;
                const pm = freeP ? 0 : m;
                const depth = (p.e - .5) * logoScale * (.1 * (1 - pm) + .014);
                const turn = smoothX * .3 + Math.sin(t * .16) * .025;
                // Our delta: gentle ambient drift on the formed mark so it shimmers, not static.
                const wob = pm * logoScale * .0035;
                const x = (target.x + (p.c - .5) * .0015) * logoScale * pm + sx * (1 - pm) + Math.sin(t * .6 + p.a * 6.3) * wob;
                const y = (target.y + (p.d - .5) * .0015) * logoScale * pm + sy * (1 - pm) + Math.cos(t * .5 + p.b * 6.3) * wob;
                const baseX = cx + x * Math.cos(turn) + depth * Math.sin(turn);
                const baseY = cy + y + depth * smoothY * .22;
                if (running) {
                    let targetX = 0, targetY = 0;
                    if (pointerPresent) {
                        const dx = baseX - (pointerX + 1) * w / 2;
                        const dy = baseY - (pointerY + 1) * h / 2;
                        const distance = Math.hypot(dx, dy);
                        const proximity = Math.max(0, 1 - distance / halfWidth);
                        let influence = proximity * proximity * (3 - 2 * proximity);
                        let normalX = distance > .01 ? dx / distance : Math.cos(p.a);
                        let normalY = distance > .01 ? dy / distance : Math.sin(p.a);
                        for (const section of wake) {
                            if (baseX < section.minX || baseX > section.maxX || baseY < section.minY || baseY > section.maxY) continue;
                            const u = Math.max(0, Math.min(1, ((baseX - section.x) * section.dx + (baseY - section.y) * section.dy) / section.length2));
                            const offsetX = baseX - section.x - section.dx * u;
                            const offsetY = baseY - section.y - section.dy * u;
                            const radius = section.r0 + (section.r1 - section.r0) * u;
                            const distance2 = offsetX * offsetX + offsetY * offsetY;
                            if (distance2 >= radius * radius) continue;
                            const d = Math.sqrt(distance2), q = 1 - d / radius;
                            const weight = q * q * (3 - 2 * q) * section.fade;
                            // Strongest section wins, avoiding extra force at loops/crossings.
                            if (weight > influence) {
                                influence = weight;
                                const length = Math.sqrt(section.length2);
                                normalX = d > .01 ? offsetX / d : -section.dy / length;
                                normalY = d > .01 ? offsetY / d : section.dx / length;
                            }
                        }
                        const strength = influence * halfWidth * (.6 + flowSpeed * .9);
                        targetX = normalX * strength;
                        targetY = normalY * strength;
                    }
                    // Fast local push and a soft return leave a short, fluid trail.
                    const response = 1 - Math.exp(-elapsedSeconds * (targetX || targetY ? 18 : 4.5));
                    pushX[i] += (targetX - pushX[i]) * response;
                    pushY[i] += (targetY - pushY[i]) * response;
                }
                const px = baseX + (reduceMotion ? 0 : pushX[i]);
                const py = baseY + (reduceMotion ? 0 : pushY[i]);
                // Our delta: word particles slightly larger/brighter than the free nebula ones.
                const size = Math.max(.32, Math.min(1.35, logoScale / 900)) * (.4 + p.e * .8) * (freeP ? 1 : 1.25);
                dot(px, py, size, `hsla(${190 + p.c * 95},88%,${(freeP ? 60 : 81) + p.e * 12}%,${freeP ? .26 + p.d * .22 : .57 + p.d * .33})`);
                if (i % 155 === 0) glow(px, py, 3 + p.e * 5, "168,204,255", .35);
            }
            ctx.restore();
        };
        frame = requestAnimationFrame(render);
        return () => { dead = true; cancelAnimationFrame(frame); observer.disconnect(); intersection.disconnect(); reduced.removeEventListener("change", onPreference); canvas.removeEventListener("pointermove", move); canvas.removeEventListener("pointerleave", leave); canvas.removeEventListener("pointerdown", activate); canvas.removeEventListener("pointerup", endTouch); canvas.removeEventListener("pointercancel", leave); canvas.removeEventListener("blur", leave); canvas.removeEventListener("keydown", key); };
    }, []);
    return (
        <Box
            component="canvas" ref={canvasRef as any} tabIndex={0} role="img"
            aria-label="Neptune — an interactive particle nebula that forms the Neptune wordmark. Move the pointer or use arrow keys; click, Enter or Space to scatter and reassemble."
            className={className}
            sx={{
                display: "block", width: "100%", height: "100%", background: "#03070e",
                touchAction: "pan-y", outline: "none",
                "&:focus-visible": { outline: "2px solid #85caff", outlineOffset: "-4px" },
            }}
        />
    );
}
